import { memo } from 'react';

import { cn } from '../../../lib/cn';

interface AvatarCellProps {
  name: string | null | undefined;
  imageUrl?: string | null;
  subtitle?: string | null;
  className?: string;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return `${first}${last}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function hashColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-teal-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-cyan-500',
  ];
  return colors[Math.abs(hash) % colors.length] ?? 'bg-blue-500';
}

export const AvatarCell = memo(function AvatarCell({
  name,
  imageUrl,
  subtitle,
  className,
}: AvatarCellProps) {
  if (!name) {
    return <span className="text-slate-400">-</span>;
  }

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="h-8 w-8 rounded-full object-cover" />
      ) : (
        <span
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white',
            hashColor(name),
          )}
        >
          {getInitials(name)}
        </span>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-secondary-950 truncate">{name}</p>
        {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
      </div>
    </div>
  );
});
